import { useAuthenticator } from '@aws-amplify/ui-react';
import DriversRank from '../../components/DriversRank/DriversRank';
import FleetHealth from '../../components/Dashboard/FleetHealth/FleetHealth';
import AlertTypes from '../../components/Dashboard/AlertTypes/AlertTypes';
import DevicesStatus from '../../components/Dashboard/DevicesStatus/DevicesStatus';
import MapsWidget from '../../components/MapAlertsWidget/MapAlertsWidget';
import Toolbar from '../../components/common/Toolbar/Toolbar';
import TimeSelection from '../../components/common/Toolbar/TimeSelection';
import GroupSelection from '../../components/GroupSelection/GroupSelection';
import S from './HomePageStyles';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useGetCurrentUser from '../../API/hooks/users/useGetCurrentUser';
import { getLocalStorageKey } from '../../utils/storageUtils';
import VehiclesDistanceWidget from '../../components/VehiclesDistanceWidget/VehiclesDistanceWidget';
import DriversDistanceWidget from '../../components/DriversDistanceWidget/DriversDistanceWidget';
import DriversAlertsCountWidget from '../../components/DriversAlertsCountWidget/DriversAlertsCountWidget';
import { dashboardPageFilters } from '../../constants/FilterDefault';
import useLocalStorage, { useGroupFilters } from '../../API/hooks/useLocalStorage';
import DashboardMovableGrid from '../../components/Dashboard/DashboardMovableGrid/DashboardMovableGrid';
import DashboardWidgetPicker from '../../components/Dashboard/DashboardWidgetPicker/DashboardWidgetPicker';
import { DashboardWidgetId } from './dashboardWidgets';
import useDashboardLayout from './useDashboardLayout';

function HomePage(): JSX.Element {
  const { user } = useAuthenticator();
  const fleetId = useGetFleetId();
  const { data: currentUser } = useGetCurrentUser(fleetId, user.attributes?.email);
  const displayName = currentUser?.name || user.attributes?.email || user.username;
  const layoutUserId = user.attributes?.email || user.username || '';

  const [groupFilters, setGroupFilters] = useGroupFilters();
  const dashboardPeriodKey = getLocalStorageKey('dashboard-selected-period');
  const [filters, setFilters] = useLocalStorage(dashboardPeriodKey, dashboardPageFilters);
  const {
    visibleWidgetIds,
    hiddenWidgetIds,
    moveWidget,
    removeWidget,
    addWidget,
  } = useDashboardLayout(fleetId, layoutUserId);

  const getWelcomeText = (): string => {
    const curentHour = (new Date()).getHours();
    let period = '';
    if (curentHour >= 0 && curentHour < 12) {
      period = 'morning';
    } else if (curentHour >= 12 && curentHour < 18) {
      period = 'afternoon';
    } else {
      period = 'evening';
    }
    return `Good ${period}, ${displayName} ✌`;
  };

  const isAllTime = filters.period[0] === 'custom' && filters.startDate?.[0] === new Date(0).toISOString();
  const normalizedPeriod = isAllTime ? 'all' : filters.period[0];

  const renderWidget = (
    widgetId: DashboardWidgetId,
    actions: { onMove: () => void; onRemove: () => void },
  ): JSX.Element => {
    switch (widgetId) {
      case 'alertTypes':
        return (
          <AlertTypes
            timeRange={filters}
            groupFilters={groupFilters || {}}
            onMove={actions.onMove}
            onRemove={actions.onRemove}
          />
        );
      case 'devicesStatus':
        return (
          <DevicesStatus
            groupFilters={groupFilters || {}}
            onMove={actions.onMove}
            onRemove={actions.onRemove}
          />
        );
      case 'driversRank':
        return (
          <DriversRank
            timeRange={filters}
            groupFilters={groupFilters || {}}
            onMove={actions.onMove}
            onRemove={actions.onRemove}
          />
        );
      case 'vehiclesDistance':
        return (
          <VehiclesDistanceWidget
            fleetId={fleetId}
            period={normalizedPeriod}
            groupFilters={groupFilters || {}}
          />
        );
      case 'driversDistance':
        return (
          <DriversDistanceWidget
            fleetId={fleetId}
            period={normalizedPeriod}
            groupFilters={groupFilters || {}}
          />
        );
      case 'driversAlertsCount':
        return (
          <DriversAlertsCountWidget
            fleetId={fleetId}
            period={normalizedPeriod}
            groupFilters={groupFilters || {}}
          />
        );
      default:
        throw new Error(`Unsupported dashboard widget: ${widgetId}`);
    }
  };

  return (
    <S.Wrapper>
      <div className="header">
        <Toolbar title={getWelcomeText()} />
        <div className="wrapper">
          <DashboardWidgetPicker hiddenWidgetIds={hiddenWidgetIds} onAdd={addWidget} />
          <TimeSelection allTimeOption customTimeBlocked filters={filters} setFilters={setFilters} />
          <GroupSelection filters={groupFilters || {}} setFilters={setGroupFilters} />
        </div>
      </div>
      <div className="widgets-container">
        <MapsWidget fleetId={fleetId} groupFilters={groupFilters || {}} />
        <S.Row>
          <FleetHealth timeRange={filters} groupFilters={groupFilters || {}} />
        </S.Row>
        <DashboardMovableGrid
          widgetIds={visibleWidgetIds}
          onMove={moveWidget}
          onRemove={removeWidget}
          renderWidget={renderWidget}
        />
      </div>
    </S.Wrapper>
  );
}

export default HomePage;
